import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useApi } from "@ferrivia/auth-web";
import { useChatPoll } from "@ferrivia/chat-ui";
import { useTranslation } from "@ferrivia/i18n";
import { ChatSummaryContext, type ChatSummaryState, type ChatSummaryValue } from "./useChatSummary";

/**
 * One chat-summary poll for the whole app.
 *
 * Deliberately a provider rather than a hook each consumer calls: the unread
 * count now reaches two surfaces — the chat tab's badge and home's pinned
 * notification — and a second consumer calling a self-polling hook would have
 * meant a second request on the same cadence for the same number, plus two
 * copies of it that could disagree mid-flight. It is one fact, so it is
 * fetched once.
 *
 * Single cadence regardless of focus: the badge is visible on every tab, not
 * just the chat screen (whose own useChatThread poll runs at the faster
 * focused cadence).
 */
export function ChatSummaryProvider({ children }: { children: ReactNode }) {
  const api = useApi();
  const { t } = useTranslation("client");
  const [state, setState] = useState<ChatSummaryState>({ status: "loading" });
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      const summary = await api.chat.summary();
      if (!cancelledRef.current) {
        setState({ status: "success", summary });
      }
    } catch (err) {
      if (!cancelledRef.current) {
        setState((prev) =>
          prev.status === "success"
            ? prev
            : {
                status: "error",
                error: err instanceof Error ? err.message : t("chatSummary.loadError", "Chat-yhteenvedon lataus epäonnistui"),
              },
        );
      }
      throw err;
    }
  }, [api, t]);

  const { poll } = useChatPoll(fetchSummary, { focusedIntervalMs: 45_000, backgroundIntervalMs: 45_000 });

  const value = useMemo<ChatSummaryValue>(() => ({ state, refresh: poll }), [state, poll]);

  return <ChatSummaryContext.Provider value={value}>{children}</ChatSummaryContext.Provider>;
}
